// src/App.jsx
// Root component — wires routing between Home catalogue and BookDetail page.
// Last updated: June 9, 2026

import React, { useState, useEffect } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import "./styles/global.css";

import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import AdminRoute from "./components/AdminRoute";
import Home from "./pages/Home";
import BookDetailPage from "./pages/BookDetailPage";
import Admin from "./pages/Admin";
import MyLibrary from "./pages/MyLibrary";
import ReaderPage from "./pages/ReaderPage";
import SearchPage from "./pages/SearchPage";
import Login from "./pages/Login";
import Toast from "./components/Toast";
import CartDrawer from "./components/CartDrawer";
import Store from "./pages/Store";
import Cart from "./pages/Cart";
import OrderSuccess from "./pages/OrderSuccess";
import MyOrders from "./pages/MyOrders";
import OrderDetail from "./pages/OrderDetail";

import { useBooks, useFilteredBooks } from "./hooks/useBooks";
import { downloadFile } from "./utils/download";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./components/CartContext";

export default function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <AppContent />
      </CartProvider>
    </AuthProvider>
  );
}

function AppContent() {
  const location = useLocation();
  // True when the reader is open — hide Navbar so reader owns the full viewport
  const isReaderRoute = location.pathname.endsWith("/read");

  const { books, loading, error, recordDownload, addBook, updateBook, deleteBook } = useBooks();
  const [toast, setToast] = useState("");

  // Global theme state synced with localStorage and document body
  const [theme, setTheme] = useState(() => localStorage.getItem("reader_theme") || "light");

  useEffect(() => {
    document.body.className = `${theme}-theme`;
    localStorage.setItem("reader_theme", theme);
  }, [theme]);

  function toggleTheme() {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  }

  // Called by BookDetailPage download button
  function handleDownload(book, format = "pdf") {
    downloadFile(book, format);
    recordDownload(book.id);
    setToast(`"${book.title}" downloaded!`);
  }

  return (
    <>
      {/* Navbar — hidden while reader is open */}
      {!isReaderRoute && (
        <Navbar
          books={books}
          theme={theme}
          toggleTheme={toggleTheme}
        />
      )}

      {/* Database connection error banner — also hidden in reader */}
      {error && !isReaderRoute && (
        <div style={{
          background: "#fff0f0", borderBottom: "1px solid #ffc0c0",
          padding: "0.75rem 2rem", fontSize: "0.88rem", color: "#a33",
        }}>
          <strong>Database error:</strong> {error} —{" "}
          <span style={{ color: "#555" }}>
            Check your API connection or Cloudflare D1 project logs.
          </span>
        </div>
      )}

      <Routes>
        <Route
          path="/"
          element={
            <Home
              books={books}
              loading={loading}
            />
          }
        />
        <Route
          path="/login"
          element={<Login />}
        />
        <Route
          path="/book/:bookId"
          element={
            <BookDetailPage
              books={books}
              loading={loading}
              onDownload={handleDownload}
            />
          }
        />
        <Route
          path="/book/:bookId/read"
          element={
            <ReaderPage
              books={books}
              loading={loading}
              theme={theme}
              setTheme={setTheme}
            />
          }
        />
        <Route
          path="/my-library"
          element={<MyLibrary books={books} />}
        />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <Admin
                books={books}
                loading={loading}
                addBook={addBook}
                updateBook={updateBook}
                deleteBook={deleteBook}
              />
            </AdminRoute>
          }
        />
        <Route
          path="/search"
          element={
            <SearchPage
              books={books}
              loading={loading}
            />
          }
        />
        <Route
          path="/store"
          element={<Store />}
        />
        <Route
          path="/cart"
          element={<Cart />}
        />
        <Route
          path="/order-success"
          element={<OrderSuccess />}
        />
        <Route
          path="/orders"
          element={<MyOrders />}
        />
        <Route
          path="/orders/:orderId"
          element={<OrderDetail />}
        />
      </Routes>

      {/* Footer — hidden in reader view */}
      {!isReaderRoute && <Footer />}

      {/* Slide-in cart drawer — hidden in reader view */}
      {!isReaderRoute && <CartDrawer />}

      {/* Download toast */}
      <Toast message={toast} onDone={() => setToast("")} />

      {/* Shimmer keyframe */}
      <style>{`
        @keyframes shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @keyframes fadeUp {
          from { opacity:0; transform:translateY(10px); }
          to   { opacity:1; transform:translateY(0); }
        }
      `}</style>
    </>
  );
}
// test auto-deploy Tue Jun  9 04:52:16 IST 2026
